import { Modal } from '@components/Modal'
import {
  PREMIO_RA_CATEGORY,
  PREMIO_RA_VOTING_URL,
} from '@constants/premioReclameAqui'
import { openWebBrowser } from '@utils/openWebBrowser'
import { LinearGradient } from 'expo-linear-gradient'
import { useTranslation } from 'react-i18next'
import { Image, Pressable } from 'react-native'
import { Text, View, YStack, useMedia } from 'tamagui'
import awardLogo from '@assets/premio-ra-2026/logo-ra26-branco-horizontal.png'
import mascotWithTrophy from '@assets/premio-ra-2026/ervilho-smoking-trofeu.png'
import mascotFace from '@assets/premio-ra-2026/ervilho-rosto.png'

// Cores da campanha do Prêmio RA 2026: não existem no design system porque
// valem só para estas peças e saem do ar com a votação.
const CAMPAIGN_GRADIENT = ['#000000', '#00110D', '#013D2B'] as const
const CTA_BACKGROUND = '#B0DE2E'
const CTA_LABEL = '#0A1400'
const BODY_COLOR = '#E6F0D5'
const DISMISS_COLOR = '#9FBF66'

interface IPremioReclameAquiVoteModalProps {
  open: boolean
  onClose: () => void
}

export function PremioReclameAquiVoteModal({
  open,
  onClose,
}: IPremioReclameAquiVoteModalProps) {
  const { t } = useTranslation('PremioReclameAqui')
  const media = useMedia()
  const isDesktop = media.gtSm

  function handleVote() {
    openWebBrowser(PREMIO_RA_VOTING_URL, true)
    onClose()
  }

  const callToAction = (
    <Pressable
      testID="premio-ra-vote-button"
      accessibilityRole="button"
      accessibilityLabel={t('cta')}
      accessibilityHint={t('accessibility.ctaHint')}
      onPress={handleVote}
      style={{ alignSelf: isDesktop ? 'flex-start' : 'stretch' }}
    >
      <View
        backgroundColor={CTA_BACKGROUND}
        br="$border-radius-sm"
        height={48}
        paddingHorizontal="$spacing-inset-sm"
        alignItems="center"
        justifyContent="center"
        cursor="pointer"
      >
        <Text ff="$mulishBold" fontWeight="700" fontSize={16} color={CTA_LABEL}>
          {isDesktop ? t('cta') : t('ctaShort')}
        </Text>
      </View>
    </Pressable>
  )

  const dismiss = (
    <Pressable
      testID="premio-ra-dismiss-button"
      accessibilityRole="button"
      accessibilityLabel={t('dismiss')}
      onPress={onClose}
    >
      <View
        height={48}
        alignItems={isDesktop ? 'flex-start' : 'center'}
        justifyContent="center"
        cursor="pointer"
      >
        <Text
          ff="$mulishBold"
          fontWeight="700"
          fontSize={14}
          color={DISMISS_COLOR}
        >
          {t('dismiss')}
        </Text>
      </View>
    </Pressable>
  )

  return (
    <Modal
      open={open}
      onClose={onClose}
      maxWidth={640}
      backdropOpacity={0.64}
      snapPointsMode="fit"
      borderRadius="$border-radius-sm"
    >
      <View
        testID="premio-ra-vote-modal"
        width="100%"
        overflow="hidden"
        br="$border-radius-sm"
        minHeight={isDesktop ? 440 : undefined}
      >
        <LinearGradient
          colors={CAMPAIGN_GRADIENT}
          locations={isDesktop ? [0, 0.54, 1] : [0, 0.46, 1]}
          start={isDesktop ? { x: 0, y: 0 } : { x: 0.2, y: 0 }}
          end={isDesktop ? { x: 1, y: 1 } : { x: 0.8, y: 1 }}
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
        />

        {isDesktop && (
          <Image
            source={mascotWithTrophy}
            alt=""
            accessibilityElementsHidden
            importantForAccessibility="no"
            style={{
              position: 'absolute',
              right: 12,
              bottom: -14,
              width: 186,
              height: 336,
              resizeMode: 'contain',
            }}
          />
        )}

        <YStack
          gap="$spacing-inset-xs"
          padding={isDesktop ? 40 : 20}
          paddingBottom={isDesktop ? 40 : 28}
          maxWidth={isDesktop ? 380 : undefined}
        >
          <View
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Image
              source={awardLogo}
              alt={t('accessibility.awardLogo')}
              style={{
                width: isDesktop ? 188 : 150,
                height: isDesktop ? 27 : 21,
                resizeMode: 'contain',
              }}
            />
            {!isDesktop && (
              <Image
                source={mascotFace}
                alt=""
                accessibilityElementsHidden
                importantForAccessibility="no"
                style={{ width: 46, height: 52, resizeMode: 'contain' }}
              />
            )}
          </View>

          <Text
            ff="$mulishBold"
            fontWeight="700"
            fontSize={isDesktop ? 28 : 22}
            lineHeight={isDesktop ? 30 : 24}
            color="#FFFFFF"
          >
            {t('title')}
          </Text>

          <Text
            ff="$mulishRegular"
            fontSize={isDesktop ? 15 : 14}
            lineHeight={isDesktop ? 22 : 21}
            color={BODY_COLOR}
          >
            {t('description', { category: PREMIO_RA_CATEGORY })}
          </Text>

          <YStack gap="$spacing-inset-xxs">
            {callToAction}
            {dismiss}
          </YStack>
        </YStack>
      </View>
    </Modal>
  )
}
